import { useMutateRequest } from "@ch/api/useMutation";
import { UsersControllerService } from "@ch/generated";
import useUserDetails from "@ch/helpers/hooks/useUserDetails";
import { getFcmToken } from "@ch/helpers/utils/firebase/firebase";
import { requestNotificationPermission } from "@ch/helpers/utils/firebase/requestNotificationPermission";
import { NotifyInfo } from "@ch/helpers/utils/helpers";
import { useAppSelector } from "@ch/redux-store/hooks/useAppSelector";
import { updateAppUserState } from "@ch/redux-store/user/actions";
import { useCallback, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { handleError } from "../../handleError";

/**
 * Retrieves the browser's FCM token and sends it to the backend for the logged-in user.
 * - Requests notification permission first
 * - Only proceeds if permission is granted
 * - Weekly token refresh (7 days)
 */
const useFCMToken = () => {
  const { isLoggedIn, userId, user } = useUserDetails();
  const dispatch = useDispatch();
  const fcmTokenUpdatedAt = useAppSelector(
    (state) => state.user?.fcmTokenUpdatedAt,
  );

  const isSubmittingRef = useRef(false);
  const pendingTokenRef = useRef<string | undefined>(undefined);
  const lastSentTokenRef = useRef<string | undefined>(undefined);
  const registrationCacheRef = useRef<ServiceWorkerRegistration | null>(null);

  const { trigger } = useMutateRequest<
    { id: string; requestBody: { fcmToken: string } },
    void
  >({
    service: UsersControllerService.usersControllerUpdateById,
    tag: "usersControllerUpdateById",
    invalidate: false,
    showNotification: false,
    onSuccess: () => {
      if (user) {
        dispatch(
          updateAppUserState({
            currentUser: {
              ...user,
              fcmToken: pendingTokenRef.current ?? undefined,
            },
            fcmTokenUpdatedAt: new Date().toISOString(),
          }),
        );
      }
      // Track that we've sent this token
      lastSentTokenRef.current = pendingTokenRef.current ?? undefined;
      pendingTokenRef.current = undefined;
    },
  });

  const shouldRefreshToken = useCallback(() => {
    const REFRESH_TOKEN_INTERVAL = 7; // 7 days
    const DAYS_IN_MS = 1000 * 60 * 60 * 24; // 1 day

    const currentToken = user?.fcmToken;
    const tokenUpdatedAt = fcmTokenUpdatedAt
      ? new Date(fcmTokenUpdatedAt).getTime()
      : null;

    if (!currentToken) return true; // No token exists

    if (tokenUpdatedAt) {
      const daysSinceUpdate = (Date.now() - tokenUpdatedAt) / DAYS_IN_MS;
      return daysSinceUpdate >= REFRESH_TOKEN_INTERVAL; // Weekly refresh
    }

    return true;
  }, [user?.fcmToken, fcmTokenUpdatedAt]);

  const waitForRegistration = useCallback(async () => {
    // Return cached registration if available
    if (registrationCacheRef.current) {
      return registrationCacheRef.current;
    }

    const maxWaitTime = 5000;
    const checkInterval = 100;

    const found = await Promise.race<null | ServiceWorkerRegistration>([
      new Promise<null>((resolve) => {
        setTimeout(() => resolve(null), maxWaitTime);
      }),
      new Promise<ServiceWorkerRegistration | null>((resolve) => {
        const checkRegistration = () => {
          navigator.serviceWorker
            .getRegistration()
            .then((reg) => {
              if (reg) {
                resolve(reg);
              } else {
                setTimeout(checkRegistration, checkInterval);
              }
            })
            .catch(() => resolve(null));
        };
        checkRegistration();
      }),
    ]);

    if (found) {
      registrationCacheRef.current = found;
    }

    return found;
  }, []);

  useEffect(() => {
    const init = async () => {
      if (!isLoggedIn || !userId) {
        return;
      }

      if (isSubmittingRef.current) {
        return;
      }

      if (lastSentTokenRef.current === user?.fcmToken && user?.fcmToken) {
        return;
      }

      try {
        isSubmittingRef.current = true;

        if (!("Notification" in window)) {
          return;
        }

        if (Notification.permission === "denied") {
          return;
        }

        const permissionGranted = await requestNotificationPermission();
        if (!permissionGranted) {
          NotifyInfo(
            "Notifications are blocked. Please enable notifications in your browser settings to receive notifications.",
          );
          return;
        }

        if (!shouldRefreshToken()) {
          return;
        }

        // Wait for service worker registration (it's registered on window.load)
        // waitForRegistration handles caching and polling internally
        let registration = await waitForRegistration();

        if (!registration) {
          // In dev mode, try to register firebase-messaging-sw.js directly
          if (import.meta.env.DEV) {
            try {
              registration = await navigator.serviceWorker.register(
                `/firebase-messaging-sw.js?t=${Date.now()}`,
              );
            } catch {
              return;
            }
          } else {
            return;
          }
        }

        const token = await getFcmToken(registration);
        if (!token) {
          return;
        }

        // check if token actually changed or if we already sent this token
        if (token === user?.fcmToken || token === lastSentTokenRef.current) {
          // Token hasn't changed or was already sent, just update timestamp
          dispatch(
            updateAppUserState({
              fcmTokenUpdatedAt: new Date().toISOString(),
            }),
          );
          return;
        }

        // check if token is being sent to the backend
        if (pendingTokenRef.current === token) {
          return;
        }

        pendingTokenRef.current = token;

        // Send to backend
        trigger({ id: userId, requestBody: { fcmToken: token } });
      } catch (error) {
        // Handle specific error cases
        handleError(error);
      } finally {
        isSubmittingRef.current = false;
      }
    };

    void init();
  }, [
    isLoggedIn,
    userId,
    user?.fcmToken,
    dispatch,
    trigger,
    shouldRefreshToken,
    waitForRegistration,
  ]);
};

export default useFCMToken;
